import * as React from 'react';
import classnames from 'classnames';
import {
  CoreRadioButton as RadioButton,
  CoreRadioButtonKeyDownEvent as RadioButtonKeyDownEvent,
} from '../internal/WixUiCoreComponents/components/CoreRadioButton';
import { KEYS } from '../../common/keyCodes';
import { DotNavigationDataKeys, DotNavigationDataHooks } from './dataHooks';
import { st, classes } from './DotNavigation.st.css';
import {
  DotNavigationAnimation,
  DotNavigationTheme,
  DotNavigationSize as Size,
  DotNavigationProps,
  DotNavigationState,
  DotNavigationDotProps,
} from './types';

const MAX_SHORT_LIST_LENGTH = 5;
const NUM_OF_NAV_DOTS = 2;

const START_SIZES = [
  Size.Normal,
  Size.Normal,
  Size.Normal,
  Size.Small,
  Size.Tiny,
];

const MIDDLE_SIZES = [
  Size.Tiny,
  Size.Small,
  Size.Normal,
  Size.Small,
  Size.Tiny,
];

const END_SIZES = [
  Size.Tiny,
  Size.Small,
  Size.Normal,
  Size.Normal,
  Size.Normal,
];

const _isNumber = (value: any) => typeof value === 'number' && !isNaN(value);

const getFiveFromStart = (start: number) => [
  start,
  start + 1,
  start + 2,
  start + 3,
  start + 4,
];

/**
 * A dot navigation component
 */
export class DotNavigation extends React.Component<
  DotNavigationProps,
  DotNavigationState
> {
  state = {
    savedCurrentIndex: 0,
    animation: null,
  };

  static displayName = 'DotNavigation';
  static defaulAriaLabel = 'Dot Navigation';

  static defaultProps: DotNavigationProps = {
    currentIndex: 0,
    length: MAX_SHORT_LIST_LENGTH,
    onSelect(_: number) {},
    showBorder: false,
    theme: DotNavigationTheme.Dark,
    'aria-label': DotNavigation.defaulAriaLabel,
  };

  static shouldAnimate = (
    currentIndex: number,
    savedCurrentIndex: number,
    length: number,
  ) =>
    (currentIndex > 2 && currentIndex < length - 3) ||
    (currentIndex === 2 && currentIndex < savedCurrentIndex) ||
    (currentIndex === length - 3 && currentIndex > savedCurrentIndex);

  static hasCurrentIndexChanged = (
    currentIndex: number,
    savedCurrentIndex: number,
    length: number,
  ) =>
    _isNumber(currentIndex) &&
    currentIndex >= 0 &&
    currentIndex < length &&
    currentIndex !== savedCurrentIndex;

  static getLongListAnimation = (
    nextProps: DotNavigationProps,
    prevState: DotNavigationState,
  ) => {
    const { currentIndex, length } = nextProps;
    const { savedCurrentIndex } = prevState;

    return DotNavigation.hasCurrentIndexChanged(
      currentIndex,
      savedCurrentIndex,
      length,
    )
      ? {
          ...prevState,
          savedCurrentIndex: currentIndex,
          ...(DotNavigation.shouldAnimate(
            currentIndex,
            savedCurrentIndex,
            length,
          ) && {
            animation:
              currentIndex < savedCurrentIndex
                ? prevState.animation === DotNavigationAnimation.Back
                  ? DotNavigationAnimation.BackNext
                  : DotNavigationAnimation.Back
                : prevState.animation === DotNavigationAnimation.Forward
                ? DotNavigationAnimation.ForwardNext
                : DotNavigationAnimation.Forward,
          }),
        }
      : null;
  };

  static getDerivedStateFromProps = (
    nextProps: DotNavigationProps,
    prevState: DotNavigationState,
  ) =>
    nextProps.length <= MAX_SHORT_LIST_LENGTH
      ? null
      : DotNavigation.getLongListAnimation(nextProps, prevState);

  _getDot = ({ checked, size, fakeRadio = false }) => (
    <div
      {...(fakeRadio && { tabIndex: -1, 'aria-hidden': true })}
      className={classnames(classes.radio)}
    >
      <div
        className={classnames(classes.dot, {
          [classes.small]: size === Size.Small,
          [classes.tiny]: size === Size.Tiny,
          [classes.bordered]: this.props.showBorder === true,
          [classes.light]: this.props.theme === DotNavigationTheme.Light,
          [classes.checked]: checked,
          [classes.noOpacityTransition]: this.props.noOpacityTransition,
        })}
        data-hook={fakeRadio ? undefined : DotNavigationDataHooks.Dot}
      />
    </div>
  );

  _renderFakeRadio = () =>
    this._getDot({ checked: false, size: Size.Tiny, fakeRadio: true });

  _renderDot = ({
    key,
    checked,
    size,
    onChange,
    onKeyDown,
    ariaLabel,
    unstableHasInnerWrapper,
  }: DotNavigationDotProps) => (
    <RadioButton
      key={key}
      checked={checked}
      checkedIcon={this._getDot({ checked, size })}
      uncheckedIcon={this._getDot({ checked: false, size })}
      onChange={onChange}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      {...{ className: classes.extendedRadioButton }}
      unstableHasInnerWrapper={unstableHasInnerWrapper}
    />
  );

  _callOnSelect = (index: number) => () => {
    this.props.onSelect(index);
  };

  _startKeyDownHandler = (event: RadioButtonKeyDownEvent) =>
    (event.key === KEYS.ArrowLeft || event.key === KEYS.ArrowUp) &&
    event.nativeEvent.preventDefault();

  _endKeyDownHandler = (event: RadioButtonKeyDownEvent) =>
    (event.key === KEYS.ArrowRight || event.key === KEYS.ArrowDown) &&
    event.nativeEvent.preventDefault();

  _getKeyDownHandler = (value: number) =>
    value === 0
      ? this._startKeyDownHandler
      : value === this.props.length - 1
      ? this._endKeyDownHandler
      : undefined;

  _renderList = (values: number[], sizes: Size[]) =>
    values.map((value, index) =>
      this._renderDot({
        key: value,
        checked: value === this.props.currentIndex,
        size: sizes[index],
        onChange: this._callOnSelect(value),
        onKeyDown: this._getKeyDownHandler(value),
        ariaLabel: this._getAriaLabel(index),
      }),
    );

  _renderStartList = () => this._renderList(getFiveFromStart(0), START_SIZES);

  _renderEndList = () =>
    this._renderList(getFiveFromStart(this.props.length - 5), END_SIZES);

  _renderMiddleList = () =>
    this._renderList(
      getFiveFromStart(this.props.currentIndex - 2),
      MIDDLE_SIZES,
    );

  _renderLongVersion = () => (
    <div
      className={classnames(classes.radioGroup, {
        [classes.back]: this.state.animation === DotNavigationAnimation.Back,
        [classes.forward]:
          this.state.animation === DotNavigationAnimation.Forward,
        [classes.backNext]:
          this.state.animation === DotNavigationAnimation.BackNext,
        [classes.forwardNext]:
          this.state.animation === DotNavigationAnimation.ForwardNext,
      })}
    >
      {this._renderFakeRadio()}
      {!_isNumber(this.props.currentIndex) ||
      this.props.currentIndex < MAX_SHORT_LIST_LENGTH - NUM_OF_NAV_DOTS
        ? this._renderStartList()
        : this.props.currentIndex >=
          this.props.length - (MAX_SHORT_LIST_LENGTH - NUM_OF_NAV_DOTS)
        ? this._renderEndList()
        : this._renderMiddleList()}
      {this._renderFakeRadio()}
    </div>
  );

  _renderShortVersion = () => {
    let shortVersion = [];

    for (let index = 0; index < this.props.length; index++) {
      shortVersion = [
        ...shortVersion,
        this._renderDot({
          key: index,
          checked: index === this.props.currentIndex,
          size: Size.Normal,
          onChange: this._callOnSelect(index),
          ariaLabel: this._getAriaLabel(index),
        }),
      ];
    }
    return shortVersion;
  };

  _getAriaLabel = (index: number): string => {
    const ariaLabel = this.props['aria-label'];

    if (Array.isArray(ariaLabel)) {
      return index < ariaLabel.length
        ? ariaLabel[index]
        : `${DotNavigation.defaulAriaLabel} ${index}`;
    }

    return `${this.props['aria-label']} ${index}`;
  };

  _getDataAttributes = () => ({
    [DotNavigationDataKeys.ShowBorder]: this.props.showBorder,
    [DotNavigationDataKeys.Theme]: this.props.theme,
    [DotNavigationDataKeys.SavedCurrentIndex]: this.state.savedCurrentIndex,
  });

  render = () => {
    const { length, className } = this.props;
    const short = length <= MAX_SHORT_LIST_LENGTH;

    return _isNumber(length) && length > 0 ? (
      <div
        className={st(classes.root, { short }, className)}
        data-hook={this.props['data-hook']}
        {...this._getDataAttributes()}
      >
        {length <= MAX_SHORT_LIST_LENGTH
          ? this._renderShortVersion()
          : this._renderLongVersion()}
      </div>
    ) : null;
  };
}
